import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { STAGE_DOT, STATUS_STYLE, TEMP_STYLE } from "@/lib/constants";
import { logoSrc, useBranding } from "@/hooks/useData";

export const TempBadge = ({ t, className }) => (
  <span
    data-testid={`temp-badge-${t}`}
    className={cn("inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-bold tracking-wide", TEMP_STYLE[t], className)}
  >
    {t}
  </span>
);

export const StageBadge = ({ stage }) => (
  <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">
    <span className={cn("h-1.5 w-1.5 rounded-full", STAGE_DOT[stage])} />
    {stage}
  </span>
);

export const StatusBadge = ({ status }) => (
  <span className={cn("inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-semibold", STATUS_STYLE[status])}>{status}</span>
);

export const UserAvatar = ({ user, size = "sm", className }) => {
  const s = size === "lg" ? "h-11 w-11 text-sm" : size === "md" ? "h-9 w-9 text-xs" : "h-7 w-7 text-[10px]";
  return (
    <span
      title={user?.name}
      className={cn("inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white", s, className)}
      style={{ background: user?.color || "#94A3B8" }}
    >
      {user?.initials || "?"}
    </span>
  );
};

export const PageHeader = ({ title, subtitle, actions, testid }) => (
  <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
    <div>
      <h1 data-testid={testid} className="text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export const Field = ({ label, children, className, hint, required }) => (
  <label className={cn("flex flex-col gap-1.5", className)}>
    <span className="label-xs">
      {label}
      {required && <span className="text-destructive"> *</span>}
    </span>
    {children}
    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
  </label>
);

export const NSelect = ({ value, onChange, options, placeholder, testid, className, disabled }) => (
  <select
    data-testid={testid}
    value={value ?? ""}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value)}
    className={cn(
      "h-10 w-full rounded-lg border border-input bg-card px-3 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-60",
      className,
    )}
  >
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => {
      const v = typeof o === "string" ? o : o.value;
      const l = typeof o === "string" ? o : o.label;
      return (
        <option key={v} value={v}>
          {l}
        </option>
      );
    })}
  </select>
);

export const Spinner = ({ className }) => (
  <div className={cn("flex items-center justify-center py-16 text-muted-foreground", className)} data-testid="loading-spinner">
    <Loader2 className="h-6 w-6 animate-spin" />
  </div>
);

export const EmptyState = ({ icon: Icon, title, text, action, testid }) => (
  <div data-testid={testid} className="flex flex-col items-center justify-center rounded-2xl border border-dashed bg-card/50 px-6 py-14 text-center">
    {Icon && <Icon className="mb-3 h-8 w-8 text-muted-foreground" />}
    <p className="font-display font-semibold">{title}</p>
    {text && <p className="mt-1 max-w-sm text-sm text-muted-foreground">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const BrandMark = ({ compact }) => {
  const { data } = useBranding();
  return (
    <div className="flex items-center gap-2.5 min-w-0" data-testid="brand-mark">
      {data?.has_logo ? (
        <img src={logoSrc(data.logo_version)} alt="logo" className="h-9 w-9 shrink-0 rounded-lg object-contain bg-white" />
      ) : (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary font-display text-lg font-extrabold text-primary-foreground">P</span>
      )}
      {!compact && (
        <div className="min-w-0 leading-tight">
          <p className="truncate font-display text-sm font-extrabold tracking-tight">PERFORMED</p>
          <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {data?.company_name || "PERFORMED REAL ESTATE LLP"}
          </p>
        </div>
      )}
    </div>
  );
};

export const InfoRow = ({ label, value, testid }) => (
  <div className="flex flex-col gap-0.5">
    <span className="label-xs">{label}</span>
    <span data-testid={testid} className="text-sm font-medium break-words">{value || "—"}</span>
  </div>
);

export const Chip = ({ active, onClick, children, testid, className }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={cn(
      "h-8 shrink-0 rounded-full border px-3 text-xs font-semibold transition-colors",
      active ? "border-primary bg-primary text-primary-foreground" : "bg-card hover:bg-secondary",
      className,
    )}
  >
    {children}
  </button>
);
